/**
 * Global F12 "Paste from Previous Row" Feature
 * 
 * This utility provides a global keyboard shortcut (F12) that allows users to
 * copy values from the same field in the previous row to the currently focused field.
 * 
 * Features:
 * - Zero manual wiring: works automatically for all editable fields
 * - Supports HTML inputs, textareas, MUI TextField, MUI Autocomplete, date pickers
 * - Automatically detects row boundaries (tables, MUI tables, generic lists)
 * - Uses stable field signatures (name, id, aria-label, placeholder, or DOM path)
 * - Properly triggers React state updates via native setters and events
 * - Stores last entered values for each field type to enable pasting even without previous row
 * - High-performance caching with IndexedDB persistence and 30-minute TTL
 * 
 * Dependencies (install with: npm install localforage quick-lru):
 *   - localforage: Fast IndexedDB storage (better than localStorage)
 *   - quick-lru: Fast in-memory LRU cache with TTL support
 * 
 * Usage:
 *   import { installF12PasteFromPreviousRow } from './utils/f12PasteFromPreviousRow';
 *   
 *   useEffect(() => {
 *     const uninstall = installF12PasteFromPreviousRow();
 *     return uninstall;
 *   }, []);
 */

// Try to import libraries, fallback to custom implementation if not available
let localforage = null;
let QuickLRU = null;

// Import quick-lru (ESM module) - use try-catch to handle if not installed
// Note: If quick-lru is not installed, this will be null and we'll use Map fallback
try {
    // eslint-disable-next-line
    const quickLruModule = require('quick-lru');
    // quick-lru exports as default in ESM, but require() might wrap it
    QuickLRU = quickLruModule.default || quickLruModule;
    
    // Verify it's actually a constructor function
    if (typeof QuickLRU !== 'function') {
        // Try to find the actual export
        if (quickLruModule.QuickLRU && typeof quickLruModule.QuickLRU === 'function') {
            QuickLRU = quickLruModule.QuickLRU;
        } else {
            QuickLRU = null;
        }
    }
} catch (e) {
    // Library not installed or require() failed (ESM modules don't work with require)
    // This is expected - quick-lru is ESM-only and needs to be imported differently
    QuickLRU = null;
}

// Dynamic imports for localforage (works with CommonJS)
try {
    // eslint-disable-next-line
    const localforageModule = require('localforage');
    localforage = localforageModule.default || localforageModule;
} catch (e) {
    // Library not installed, will use localStorage fallback
}

/**
 * High-Performance Cache Manager with Persistence
 * Uses localforage (IndexedDB) + quick-lru (in-memory LRU cache)
 * Features:
 * - In-memory LRU cache for O(1) fast access with automatic eviction
 * - IndexedDB for persistence (faster than localStorage)
 * - Background sync using requestIdleCallback
 * - TTL (30 minutes) with automatic cleanup
 * - Batch operations for performance
 * 
 * To install dependencies:
 *   npm install localforage quick-lru
 */
class HighPerformanceCache {
    constructor() {
        // Use quick-lru if available (LRU with TTL), otherwise use Map
        if (QuickLRU && typeof QuickLRU === 'function') {
            try {
                // LRU cache with max 1000 entries and 30-minute TTL
                this.memoryCache = new QuickLRU({ maxSize: 1000, maxAge: 30 * 60 * 1000 });
                this.useQuickLRU = true;
                console.log('[F12 Cache] ✅ Using quick-lru for in-memory cache (LRU with TTL)');
            } catch (e) {
                console.warn('[F12 Cache] Failed to initialize quick-lru, using Map fallback:', e.message);
                this.memoryCache = new Map();
                this.cacheTimestamps = new Map();
                this.useQuickLRU = false;
            }
        } else {
            // Fallback to Map with manual TTL tracking
            this.memoryCache = new Map();
            this.cacheTimestamps = new Map();
            this.useQuickLRU = false;
            console.log('[F12 Cache] Using Map fallback (install quick-lru for LRU cache: npm install quick-lru)');
        }
        
        // Use localforage for persistence if available (IndexedDB), otherwise localStorage
        if (localforage) {
            this.storage = localforage.createInstance({
                name: 'f12_paste_cache',
                storeName: 'field_values'
            });
            console.log('[F12 Cache] Using localforage (IndexedDB) for persistence');
        } else {
            this.storage = null;
            console.log('[F12 Cache] Using localStorage fallback (install localforage for better performance)');
        }
        
        // Storage key prefix
        this.STORAGE_KEY = 'f12_paste_cache';
        this.TTL_MS = 30 * 60 * 1000; // 30 minutes
        
        // Batch operations
        this.pendingWrites = new Map();
        this.writeTimer = null;
        this.isSyncing = false;
        
        // Load from storage on initialization
        this.loadFromStorage();
        
        // Start background cleanup and sync
        this.startBackgroundTasks();
        
        console.log('[F12 Cache] Cache initialized with Map (will upgrade to quick-lru if available)');
    }
    
    
    /**
     * Load data from storage (runs once on init)
     * Uses IndexedDB (localforage) if available, otherwise localStorage
     */
    async loadFromStorage() {
        try {
            if (this.storage && localforage) {
                // Use localforage (IndexedDB) - async
                const keys = await this.storage.keys();
                const now = Date.now();
                let loaded = 0;
                let expired = 0;
                
                // Load all keys in parallel (fast)
                const entries = await Promise.all(
                    keys.map(async (key) => {
                        const entry = await this.storage.getItem(key);
                        return { key, entry };
                    })
                );
                
                // Filter and load valid entries
                for (const { key, entry } of entries) {
                    if (entry && entry.timestamp && (now - entry.timestamp) < this.TTL_MS) {
                        this.memoryCache.set(key, entry.value);
                        loaded++;
                    } else {
                        expired++;
                        // Remove expired entry
                        await this.storage.removeItem(key);
                    }
                }
                
                console.log(`[F12 Cache] Loaded ${loaded} entries, expired ${expired} entries from IndexedDB`);
            } else {
                // Fallback to localStorage
                const stored = localStorage.getItem(this.STORAGE_KEY);
                if (stored) {
                    const data = JSON.parse(stored);
                    const now = Date.now();
                    let loaded = 0;
                    let expired = 0;
                    
                    // Load valid entries (not expired)
                    for (const [key, entry] of Object.entries(data)) {
                        if (entry.timestamp && (now - entry.timestamp) < this.TTL_MS) {
                            this.memoryCache.set(key, entry.value);
                            if (!QuickLRU) {
                                this.cacheTimestamps.set(key, entry.timestamp);
                            }
                            loaded++;
                        } else {
                            expired++;
                        }
                    }
                    
                    console.log(`[F12 Cache] Loaded ${loaded} entries, expired ${expired} entries from localStorage`);
                }
            }
        } catch (error) {
            console.error('[F12 Cache] Error loading from storage:', error);
        }
    }
    
    /**
     * Save to storage (batched, non-blocking)
     * Uses IndexedDB (localforage) if available, otherwise localStorage
     */
    saveToStorage() {
        if (this.isSyncing) return;
        this.isSyncing = true;
        
        // Use requestIdleCallback for background sync (non-blocking)
        const sync = async () => {
            try {
                const now = Date.now();
                
                if (this.storage && localforage) {
                    // Use localforage (IndexedDB) - async, faster than localStorage
                    const entries = Array.from(this.memoryCache.entries());
                    
                    // Save all entries in parallel (very fast)
                    await Promise.all(
                        entries.map(([key, value]) => 
                            this.storage.setItem(key, { value, timestamp: now })
                        )
                    );
                    
                    this.isSyncing = false;
                } else {
                    // Fallback to localStorage
                    const data = {};
                    
                    // Only save non-expired entries
                    for (const [key, value] of this.memoryCache.entries()) {
                        data[key] = {
                            value,
                            timestamp: this.useQuickLRU ? now : (this.cacheTimestamps.get(key) || now)
                        };
                    }
                    
                    // Save to localStorage
                    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
                    this.isSyncing = false;
                }
            } catch (error) {
                console.error('[F12 Cache] Error saving to storage:', error);
                this.isSyncing = false;
            }
        };
        
        // Use requestIdleCallback if available, otherwise setTimeout
        if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(sync, { timeout: 1000 });
        } else {
            setTimeout(sync, 0);
        }
    }
    
    /**
     * Get value from cache (fast in-memory lookup, O(1))
     */
    get(key) {
        const value = this.memoryCache.get(key);
        
        // If using Map fallback, check TTL
        if (!this.useQuickLRU && value) {
            const timestamp = this.cacheTimestamps.get(key);
            if (timestamp && (Date.now() - timestamp) >= this.TTL_MS) {
                // Expired, remove it
                this.memoryCache.delete(key);
                this.cacheTimestamps.delete(key);
                return undefined;
            }
        }
        
        return value;
    }
    
    /**
     * Check if key exists in cache (fast in-memory lookup, O(1))
     */
    has(key) {
        if (!this.useQuickLRU) {
            // For Map fallback, check TTL
            const timestamp = this.cacheTimestamps.get(key);
            if (timestamp && (Date.now() - timestamp) >= this.TTL_MS) {
                this.memoryCache.delete(key);
                this.cacheTimestamps.delete(key);
                return false;
            }
        }
        return this.memoryCache.has(key);
    }
    
    /**
     * Set value in cache (fast in-memory, async persistence)
     */
    set(key, value) {
        // Fast in-memory update (synchronous, O(1))
        this.memoryCache.set(key, value);
        
        // Track timestamp for Map fallback
        if (!this.useQuickLRU) {
            this.cacheTimestamps.set(key, Date.now());
        }
        
        // Schedule background persistence (non-blocking)
        this.pendingWrites.set(key, value);
        this.scheduleWrite();
    }
    
    /**
     * Schedule batched write to localStorage
     */
    scheduleWrite() {
        if (this.writeTimer) return;
        
        // Batch writes - wait for idle time
        this.writeTimer = setTimeout(() => {
            this.saveToStorage();
            this.pendingWrites.clear();
            this.writeTimer = null;
        }, 100); // Small delay to batch multiple writes
    }
    
    /**
     * Remove expired entries (runs in background)
     */
    async cleanup() {
        const now = Date.now();
        let removed = 0;
        
        try {
            if (this.storage && localforage) {
                // Clean IndexedDB
                const keys = await this.storage.keys();
                
                for (const key of keys) {
                    const entry = await this.storage.getItem(key);
                    if (entry && entry.timestamp && (now - entry.timestamp) >= this.TTL_MS) {
                        await this.storage.removeItem(key);
                        this.memoryCache.delete(key);
                        removed++;
                    }
                }
                
                if (removed > 0) {
                    console.log(`[F12 Cache] Cleaned ${removed} expired entries from IndexedDB`);
                }
            } else {
                // Clean localStorage and in-memory cache
                const stored = localStorage.getItem(this.STORAGE_KEY);
                if (stored) {
                    const data = JSON.parse(stored);
                    const cleaned = {};
                    
                    for (const [key, entry] of Object.entries(data)) {
                        if (entry.timestamp && (now - entry.timestamp) < this.TTL_MS) {
                            cleaned[key] = entry;
                        } else {
                        removed++;
                        this.memoryCache.delete(key);
                        if (!this.useQuickLRU) {
                            this.cacheTimestamps.delete(key);
                        }
                        }
                    }
                    
                    if (removed > 0) {
                        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cleaned));
                        console.log(`[F12 Cache] Cleaned ${removed} expired entries from localStorage`);
                    }
                }
            }
        } catch (error) {
            console.error('[F12 Cache] Error during cleanup:', error);
        }
    }
    
    /**
     * Start background tasks (cleanup and sync)
     */
    startBackgroundTasks() {
        // Cleanup expired entries every 5 minutes
        setInterval(() => {
            this.cleanup();
        }, 5 * 60 * 1000);
        
        // Force sync to localStorage every 2 minutes (in case of crashes)
        setInterval(() => {
            if (this.memoryCache.size > 0) {
                this.saveToStorage();
            }
        }, 2 * 60 * 1000);
        
        // Cleanup on page unload (save current state)
        window.addEventListener('beforeunload', () => {
            this.saveToStorage();
        });
    }
    
    /**
     * Clear all cache
     */
    async clear() {
        this.memoryCache.clear();
        this.pendingWrites.clear();
        
        if (!this.useQuickLRU) {
            this.cacheTimestamps.clear();
        }
        
        if (this.storage && localforage) {
            await this.storage.clear();
        } else {
            localStorage.removeItem(this.STORAGE_KEY);
        }
    }
    
    /**
     * Get cache size
     */
    size() {
        return this.memoryCache.size;
    }
    
    /**
     * Get all keys
     */
    keys() {
        return Array.from(this.memoryCache.keys());
    }
}

// Create singleton instance
const fieldValueStorage = new HighPerformanceCache();

/**
 * Creates a storage key from a field signature
 */
function getStorageKey(signature) {
    if (!signature) return null;
    return `${signature.type}:${signature.value}`;
}

/**
 * Detects if an element is an editable input field
 */
function isEditableField(element) {
    if (!element) return false;
    
    // Check if disabled or readOnly
    if (element.disabled || element.readOnly) return false;
    
    const tagName = element.tagName?.toLowerCase();
    
    // Native select element
    if (tagName === 'select') {
        return true;
    }
    
    // Check if it's a password field (skip for security)
    if (element.type === 'password') return false;
    
    // Native input/textarea (including date, time, etc.)
    if (tagName === 'input' || tagName === 'textarea') {
        return true;
    }
    
    // Contenteditable
    if (element.contentEditable === 'true') {
        return true;
    }
    
    // MUI Select: check for select element inside
    if (element.closest('.MuiSelect-root, .MuiSelect-select')) {
        const select = element.closest('.MuiSelect-root, .MuiSelect-select').querySelector('select');
        if (select && !select.disabled) {
            return true;
        }
    }
    
    // MUI TextField: find the actual input element inside
    // MUI TextField wraps input in a structure like: .MuiInputBase-root > input
    if (element.closest('.MuiInputBase-root')) {
        const input = element.closest('.MuiInputBase-root').querySelector('input, textarea, select');
        if (input && !input.disabled && !input.readOnly && input.type !== 'password') {
            return true;
        }
    }
    
    // MUI Autocomplete: find the input inside
    if (element.closest('.MuiAutocomplete-root')) {
        const input = element.closest('.MuiAutocomplete-root').querySelector('input');
        if (input && !input.disabled && !input.readOnly && input.type !== 'password') {
            return true;
        }
    }
    
    // Date pickers (custom date picker components that use inputs)
    // Check for common date picker classes
    if (element.closest('.react-datepicker-wrapper, .MuiDatePicker-root, .MuiPickersDay-root')) {
        const dateInput = element.closest('.react-datepicker-wrapper, .MuiDatePicker-root').querySelector('input');
        if (dateInput && !dateInput.disabled && !dateInput.readOnly) {
            return true;
        }
    }
    
    return false;
}

/**
 * Gets the actual input element from a potentially wrapped element (MUI components)
 */
function getActualInputElement(element) {
    if (!element) return null;
    
    const tagName = element.tagName?.toLowerCase();
    
    // If it's already a native input/textarea/select, return it
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
        return element;
    }
    
    // Check for MUI Select wrapper
    const muiSelect = element.closest('.MuiSelect-root, .MuiSelect-select');
    if (muiSelect) {
        const select = muiSelect.querySelector('select');
        if (select) return select;
        // MUI Select might use a hidden input or display value
        const hiddenInput = muiSelect.querySelector('input[type="hidden"]');
        if (hiddenInput) return hiddenInput;
    }
    
    // Check for MUI TextField wrapper
    const muiInputBase = element.closest('.MuiInputBase-root');
    if (muiInputBase) {
        const input = muiInputBase.querySelector('input, textarea, select');
        if (input) return input;
    }
    
    // Check for MUI Autocomplete wrapper
    const muiAutocomplete = element.closest('.MuiAutocomplete-root');
    if (muiAutocomplete) {
        const input = muiAutocomplete.querySelector('input');
        if (input) return input;
    }
    
    // Check for date picker wrappers
    const datePickerWrapper = element.closest('.react-datepicker-wrapper, .MuiDatePicker-root');
    if (datePickerWrapper) {
        const dateInput = datePickerWrapper.querySelector('input');
        if (dateInput) return dateInput;
    }
    
    // For contenteditable, return the element itself
    if (element.contentEditable === 'true') {
        return element;
    }
    
    return element;
}

/**
 * Detects the row container for a given element
 * 
 * Strategy:
 * 1. Look for HTML table row (<tr>) - MUST find <tr>, not <td>
 * 2. Look for MUI table row (.MuiTableRow-root)
 * 3. Look for elements inside tbody/TableBody
 * 4. Look for generic row patterns: parent with multiple similar siblings
 */
function findRowContainer(element) {
    if (!element) return null;
    
    let current = element;
    
    // Walk up the DOM tree - we MUST find a <tr> element, not a <td>
    while (current && current !== document.body) {
        const tagName = current.tagName?.toLowerCase();
        const className = typeof current.className === 'string' ? current.className : 
                         (current.className?.baseVal || '');
        const classString = typeof className === 'string' ? className : 
                           (className?.baseVal || '');
        
        // Case 1: HTML table row - THIS IS WHAT WE WANT
        if (tagName === 'tr') {
            console.log('[F12 Paste] Found <tr> row container:', current);
            return current;
        }
        
        // Case 2: MUI table row - check for class name (can be string or object)
        if (classString.includes('MuiTableRow-root')) {
            console.log('[F12 Paste] Found MUI TableRow:', current);
            return current;
        }
        
        // Case 2b: If we're in a <td>, we need to continue up to find the parent <tr>
        // Don't stop at <td> - keep looking for <tr>
        if (tagName === 'td' || tagName === 'th') {
            // Continue walking up - the <tr> should be the parent
            current = current.parentElement;
            continue;
        }
        
        // Case 2c: Check if parent is tbody and current is a direct child (likely a row)
        if (current.parentElement) {
            const parentTag = current.parentElement.tagName?.toLowerCase();
            const parentClassName = current.parentElement.className ? 
                String(current.parentElement.className) : '';
            
            if (parentTag === 'tbody' || parentClassName.includes('MuiTableBody-root')) {
                // If we're inside a tbody, check if current is a <tr>
                if (tagName === 'tr' || classString.includes('MuiTableRow')) {
                    console.log('[F12 Paste] Found row inside tbody:', current);
                    return current;
                }
                // If not, continue up to find the tr
            }
        }
        
        // Case 3: Div-based form rows (for non-table structures)
        // Look for common form row patterns
        if (classString.includes('form-row-item') || 
            classString.includes('edit-form-row-item') ||
            classString.includes('row-item') ||
            classString.includes('form-row')) {
            // Check if parent has multiple similar children (indicating it's a row container)
            if (current.parentElement) {
                const siblings = Array.from(current.parentElement.children);
                const similarSiblings = siblings.filter(sibling => {
                    const siblingClassString = typeof sibling.className === 'string' ? sibling.className :
                                             (sibling.className?.baseVal || '');
                    // Check for similar row classes
                    return siblingClassString.includes('form-row-item') ||
                           siblingClassString.includes('edit-form-row-item') ||
                           siblingClassString.includes('row-item') ||
                           (sibling.tagName === current.tagName && siblingClassString === classString);
                });
                
                // If we have multiple similar siblings, this is likely a row
                if (similarSiblings.length >= 2 && similarSiblings.includes(current)) {
                    console.log('[F12 Paste] Found div-based row container:', current);
                    return current;
                }
            }
        }
        
        // Case 4: Generic row detection (fallback)
        // Look for parent that has multiple similar siblings (likely a row container)
        if (current.parentElement) {
            const siblings = Array.from(current.parentElement.children);
            const similarSiblings = siblings.filter(sibling => {
                // Same tag name
                if (sibling.tagName !== current.tagName) return false;
                // Similar class structure (at least one class in common)
                const currentClasses = classString.split(/\s+/).filter(Boolean);
                const siblingClassString = typeof sibling.className === 'string' ? sibling.className :
                                         (sibling.className?.baseVal || '');
                const siblingClasses = siblingClassString.split(/\s+/).filter(Boolean);
                if (currentClasses.length > 0 && siblingClasses.length > 0) {
                    return currentClasses.some(c => siblingClasses.includes(c));
                }
                return true; // If no classes, assume similar if same tag
            });
            
            // If we have multiple similar siblings (at least 2), this parent might be a container
            if (similarSiblings.length >= 2 && similarSiblings.includes(current)) {
                const parentSiblings = Array.from(current.parentElement.children);
                if (parentSiblings.length >= 2) {
                    // Return if it's a <tr> or if it looks like a row container
                    if (tagName === 'tr' || classString.includes('row') || classString.includes('item')) {
                        console.log('[F12 Paste] Found generic row container:', current);
                        return current;
                    }
                }
            }
        }
        
        current = current.parentElement;
    }
    
    console.log('[F12 Paste] Could not find row container');
    return null;
}

/**
 * Finds the previous sibling row of the same type
 */
function findPreviousRow(rowContainer) {
    if (!rowContainer || !rowContainer.parentElement) {
        console.log('[F12 Paste] No row container or parent for findPreviousRow');
        return null;
    }
    
    const parent = rowContainer.parentElement;
    const siblings = Array.from(parent.children);
    const currentIndex = siblings.indexOf(rowContainer);
    
    console.log('[F12 Paste] Current row index:', currentIndex, 'Total siblings:', siblings.length);
    
    if (currentIndex <= 0) {
        console.log('[F12 Paste] No previous row (current index is', currentIndex, ')');
        return null; // No previous row
    }
    
    // Find the most recent previous sibling that looks like a row
    for (let i = currentIndex - 1; i >= 0; i--) {
        const sibling = siblings[i];
        const siblingTag = sibling.tagName?.toLowerCase();
        
        // Must be a <tr> element
        if (siblingTag !== 'tr' && !String(sibling.className || '').includes('MuiTableRow-root')) {
            console.log('[F12 Paste] Skipping sibling (not a <tr>):', siblingTag);
            continue;
        }
        
        // Check if it's the same type (same tag or same class pattern)
        const rowContainerTag = rowContainer.tagName?.toLowerCase();
        const isSameType = 
            siblingTag === rowContainerTag ||
            (sibling.className && rowContainer.className && 
             String(sibling.className).split(/\s+/).some(c => String(rowContainer.className).split(/\s+/).includes(c)));
        
        if (isSameType) {
            console.log('[F12 Paste] Found previous <tr> row at index:', i);
            return sibling;
        }
    }
    
    console.log('[F12 Paste] No matching previous row found');
    return null;
}

/**
 * Creates a stable signature for a field to identify "the same field" across rows
 * 
 * Priority order:
 * 1. name attribute
 * 2. id attribute
 * 3. aria-label attribute
 * 4. placeholder attribute
 * 5. DOM path index (position within row)
 */
function createFieldSignature(element, rowContainer) {
    if (!element || !rowContainer) return null;
    
    // Get the actual input element (for MUI components)
    const actualInput = getActualInputElement(element);
    if (!actualInput) return null;
    
    // Priority 1: name attribute
    if (actualInput.name) {
        console.log('[F12 Paste] Using name signature:', actualInput.name);
        return { type: 'name', value: actualInput.name };
    }
    
    // Priority 2: placeholder (more stable than id for MUI components with dynamic IDs)
    if (actualInput.placeholder) {
        console.log('[F12 Paste] Using placeholder signature:', actualInput.placeholder);
        return { type: 'placeholder', value: actualInput.placeholder };
    }
    
    // Priority 3: aria-label (check both input and wrapper)
    const ariaLabel = actualInput.getAttribute('aria-label') || 
                      element.getAttribute('aria-label') ||
                      element.closest('.MuiInputBase-root')?.getAttribute('aria-label');
    if (ariaLabel) {
        console.log('[F12 Paste] Using aria-label signature:', ariaLabel);
        return { type: 'aria-label', value: ariaLabel };
    }
    
    // Priority 4: DOM path (reliable for tables - calculate before id)
    // Calculate the path from row to element as an array of indices
    // For MUI TextField, we need to find the table cell (td) that contains it
    let cellElement = actualInput.closest('td, th, .MuiTableCell-root');
    if (!cellElement) {
        // Fallback: use the actual input
        cellElement = actualInput;
    }
    
    const path = [];
    let current = cellElement;
    
    while (current && current !== rowContainer) {
        if (current.parentElement) {
            const siblings = Array.from(current.parentElement.children);
            const index = siblings.indexOf(current);
            if (index >= 0) {
                path.unshift(index);
            }
        }
        current = current.parentElement;
    }
    
    if (path.length > 0) {
        console.log('[F12 Paste] Using path signature:', path.join(','));
        return { type: 'path', value: path.join(',') };
    }
    
    // Priority 5: id attribute (last resort, as MUI generates dynamic IDs like :rd5:)
    if (actualInput.id) {
        console.log('[F12 Paste] Using id signature (may not match due to dynamic IDs):', actualInput.id);
        return { type: 'id', value: actualInput.id };
    }
    
    console.log('[F12 Paste] Could not create any signature');
    return null;
}

/**
 * Finds a field in a row container matching the given signature
 */
function findMatchingField(rowContainer, signature) {
    if (!rowContainer || !signature) return null;
    
    const { type, value } = signature;
    
    // Search all editable fields in the row (including selects)
    const allInputs = rowContainer.querySelectorAll('input, textarea, select, [contenteditable="true"]');
    
    for (const input of allInputs) {
        // Skip disabled/readonly/password
        if (input.disabled || input.readOnly || input.type === 'password') continue;
        
        let matches = false;
        
        switch (type) {
            case 'name':
                matches = input.name === value;
                break;
            case 'id':
                matches = input.id === value;
                break;
            case 'aria-label':
                matches = input.getAttribute('aria-label') === value;
                break;
            case 'placeholder':
                matches = input.placeholder === value;
                break;
            case 'path':
                // Calculate path for this input's cell
                let cellElement = input.closest('td, th, .MuiTableCell-root');
                if (!cellElement) {
                    cellElement = input;
                }
                const path = [];
                let current = cellElement;
                while (current && current !== rowContainer) {
                    if (current.parentElement) {
                        const siblings = Array.from(current.parentElement.children);
                        const index = siblings.indexOf(current);
                        if (index >= 0) {
                            path.unshift(index);
                        }
                    }
                    current = current.parentElement;
                }
                matches = path.join(',') === value;
                break;
        }
        
        if (matches) {
            return input;
        }
    }
    
    // Also check MUI TextField, Select, and Autocomplete wrappers
    const muiInputs = rowContainer.querySelectorAll('.MuiInputBase-root, .MuiAutocomplete-root, .MuiSelect-root');
    for (const wrapper of muiInputs) {
        const input = wrapper.querySelector('input, textarea, select');
        if (!input || input.disabled || input.readOnly || input.type === 'password') continue;
        
        let matches = false;
        switch (type) {
            case 'name':
                matches = input.name === value;
                break;
            case 'id':
                matches = input.id === value;
                break;
            case 'aria-label':
                matches = input.getAttribute('aria-label') === value || 
                         wrapper.getAttribute('aria-label') === value;
                break;
            case 'placeholder':
                matches = input.placeholder === value;
                break;
            case 'path':
                // Calculate path for the cell containing this wrapper
                let cellElement = wrapper.closest('td, th, .MuiTableCell-root');
                if (!cellElement) {
                    cellElement = wrapper;
                }
                const path = [];
                let current = cellElement;
                while (current && current !== rowContainer) {
                    if (current.parentElement) {
                        const siblings = Array.from(current.parentElement.children);
                        const index = siblings.indexOf(current);
                        if (index >= 0) {
                            path.unshift(index);
                        }
                    }
                    current = current.parentElement;
                }
                matches = path.join(',') === value;
                break;
        }
        
        if (matches) {
            return input;
        }
    }
    
    return null;
}

/**
 * Extracts the value from a field (handles MUI components, selects, date pickers)
 */
function extractFieldValue(element) {
    console.log('[F12 Paste] Extracting value from element:', element);
    const actualInput = getActualInputElement(element);
    if (!actualInput) {
        console.log('[F12 Paste] Could not get actual input element');
        return null;
    }
    
    const tagName = actualInput.tagName?.toLowerCase();
    console.log('[F12 Paste] Actual input element:', actualInput, 'Type:', tagName, 'Current value:', actualInput.value);
    
    let extractedValue = null;
    
    // For select elements
    if (tagName === 'select') {
        extractedValue = actualInput.value || '';
        console.log('[F12 Paste] Extracted from select:', extractedValue);
        
        // Also try to get the displayed text from selected option
        if (actualInput.selectedOptions && actualInput.selectedOptions.length > 0) {
            const selectedText = actualInput.selectedOptions[0].textContent || '';
            if (selectedText) {
                console.log('[F12 Paste] Select option text:', selectedText);
                // Return the value, but we could also return text if needed
            }
        }
        return extractedValue;
    }
    
    // For contenteditable
    if (actualInput.contentEditable === 'true') {
        extractedValue = actualInput.textContent || '';
        console.log('[F12 Paste] Extracted from contenteditable:', extractedValue);
        return extractedValue;
    }
    
    // For MUI Select (might not be a native select)
    const muiSelectRoot = element.closest('.MuiSelect-root, .MuiSelect-select');
    if (muiSelectRoot && tagName !== 'select') {
        console.log('[F12 Paste] Found MUI Select');
        // Try to get value from data-value attribute or input
        const dataValue = muiSelectRoot.getAttribute('data-value') || 
                         muiSelectRoot.getAttribute('value') ||
                         muiSelectRoot.querySelector('input')?.value;
        if (dataValue) {
            extractedValue = dataValue;
            console.log('[F12 Paste] Extracted from MUI Select:', extractedValue);
            return extractedValue;
        }
        // Try to get displayed text
        const selectDisplay = muiSelectRoot.querySelector('.MuiSelect-select, [role="button"]');
        if (selectDisplay) {
            extractedValue = selectDisplay.textContent?.trim() || selectDisplay.getAttribute('data-value') || '';
            console.log('[F12 Paste] Extracted from MUI Select display:', extractedValue);
            return extractedValue;
        }
    }
    
    // For MUI Autocomplete, extract the name/label part (not the full display string)
    const autocompleteRoot = element.closest('.MuiAutocomplete-root');
    if (autocompleteRoot) {
        console.log('[F12 Paste] Found MUI Autocomplete');
        
        const inputText = actualInput.value || '';
        if (!inputText) return null;
        
        // Extract the name part from formats like:
        // "92 - בדרדקהה - 208219212" (ID - Name - AccountCaseId) -> extract "בדרדקהה"
        // "65 - מע"מ חו"ז" (ID - Name) -> extract "מע"מ חו"ז"
        const extractNameFromDisplay = (displayText) => {
            if (!displayText) return '';
            
            // Normalize whitespace: replace multiple spaces with single space, then trim
            const normalized = displayText.replace(/\s+/g, ' ').trim();
            
            // Split by " - " (space dash space) which is common in their format
            const parts = normalized.split(' - ');
            if (parts.length >= 2) {
                // Always return the second part (index 1) which is the name
                // For "92 - בדרדקהה - 208219212": parts[1] = "בדרדקהה"
                // For "65 - מע"מ חו"ז": parts[1] = "מע"מ חו"ז"
                const namePart = parts[1].trim().replace(/\s+/g, ' ');
                console.log('[F12 Paste] Extracted name from display:', namePart, 'from:', displayText);
                return namePart;
            }
            // If no " - " separator, return the whole text (normalized)
            console.log('[F12 Paste] No separator found, using full text:', normalized);
            return normalized;
        };
        
        const namePart = extractNameFromDisplay(inputText);
        extractedValue = namePart;
        return extractedValue;
    }
    
    // For date pickers (MUI DatePicker), get the formatted date value
    const datePickerRoot = element.closest('.MuiDatePicker-root');
    if (datePickerRoot) {
        const dateInput = datePickerRoot.querySelector('input');
        if (dateInput && dateInput.value) {
            extractedValue = dateInput.value;
            console.log('[F12 Paste] Extracted from MUI DatePicker:', extractedValue);
            return extractedValue;
        }
    }
    
    // For native date inputs or other date pickers
    if (actualInput.type === 'date' || element.closest('.react-datepicker-wrapper')) {
        extractedValue = actualInput.value || '';
        console.log('[F12 Paste] Extracted from date picker:', extractedValue);
        return extractedValue;
    }
    
    // Standard input/textarea
    extractedValue = actualInput.value || '';
    console.log('[F12 Paste] Extracted value (standard input):', extractedValue, 'Type:', typeof extractedValue);
    return extractedValue;
}

/**
 * Types date character by character for DatePicker
 */
function typeDateCharacterByCharacter(dateInput, value) {
    // Clear the input first
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    if (setter) {
        setter.call(dateInput, '');
    } else {
        dateInput.value = '';
    }
    
    // Dispatch input event for clearing
    const clearInputEvent = new InputEvent('input', {
        bubbles: true,
        cancelable: true,
        inputType: 'deleteContentBackward',
        data: null
    });
    dateInput.dispatchEvent(clearInputEvent);
    
    // Now type the date character by character
    const dateString = String(value);
    let charIndex = 0;
    
    const typeCharacter = () => {
        if (charIndex < dateString.length) {
            const char = dateString[charIndex];
            
            // Use native setter to append character
            if (setter) {
                const currentValue = dateInput.value || '';
                setter.call(dateInput, currentValue + char);
            } else {
                dateInput.value += char;
            }
            
            // Dispatch keydown event
            const keydownEvent = new KeyboardEvent('keydown', {
                bubbles: true,
                cancelable: true,
                key: char,
                code: char === '/' ? 'Slash' : `Digit${char}`,
                keyCode: char.charCodeAt(0)
            });
            dateInput.dispatchEvent(keydownEvent);
            
            // Dispatch keypress event
            const keypressEvent = new KeyboardEvent('keypress', {
                bubbles: true,
                cancelable: true,
                key: char,
                charCode: char.charCodeAt(0)
            });
            dateInput.dispatchEvent(keypressEvent);
            
            // Dispatch input event with insertText type - THIS IS CRITICAL for MUI DatePicker
            const inputEvent = new InputEvent('input', {
                bubbles: true,
                cancelable: true,
                inputType: 'insertText',
                data: char,
                isComposing: false
            });
            dateInput.dispatchEvent(inputEvent);
            
            // Dispatch keyup event
            const keyupEvent = new KeyboardEvent('keyup', {
                bubbles: true,
                cancelable: true,
                key: char,
                code: char === '/' ? 'Slash' : `Digit${char}`
            });
            dateInput.dispatchEvent(keyupEvent);
            
            charIndex++;
            // Small delay between characters (80ms) to simulate typing and give React time to process
            setTimeout(typeCharacter, 80);
        } else {
            // All characters typed, dispatch final change event
            setTimeout(() => {
                const changeEvent = new Event('change', { bubbles: true, cancelable: true });
                dateInput.dispatchEvent(changeEvent);
                
                console.log('[F12 Paste] DatePicker typing complete, final value:', dateInput.value);
                
                // Wait for debounce (300ms) then verify
                setTimeout(() => {
                    const finalValue = dateInput.value;
                    if (finalValue && finalValue !== 'DD/MM/YYYY' && finalValue !== dateInput.placeholder) {
                        console.log('[F12 Paste] ✅ DatePicker value successfully set:', finalValue);
                    } else {
                        console.log('[F12 Paste] ⚠️ DatePicker value may have been reset, current:', finalValue);
                    }
                }, 400);
            }, 100);
        }
    };
    
    // Start typing
    typeCharacter();
}

/**
 * Sets a value in a field and triggers React updates
 */
function setFieldValue(element, value) {
    console.log('[F12 Paste] Setting field value:', value, 'Type:', typeof value);
    
    if (value === null || value === undefined) {
        console.log('[F12 Paste] Value is null/undefined, skipping');
        return;
    }
    
    const actualInput = getActualInputElement(element);
    if (!actualInput) {
        console.log('[F12 Paste] Could not get actual input element for setting value');
        return;
    }
    
    console.log('[F12 Paste] Target input element:', actualInput, 'Current value before set:', actualInput.value);
    
    // CHECK FOR DATE PICKER FIRST - before any other handling
    // This must happen before regular input handling to prevent React from resetting the value
    
    // Primary detection: Check if placeholder indicates it's a date picker
    // "DD/MM/YYYY" is a very specific indicator for date pickers
    const placeholder = actualInput.placeholder || actualInput.getAttribute('placeholder') || '';
    const isDatePickerByPlaceholder = placeholder && 
                                      (placeholder === 'DD/MM/YYYY' || 
                                       placeholder.includes('DD/MM/YYYY') || 
                                       placeholder.includes('MM/DD/YYYY') ||
                                       placeholder.toLowerCase().includes('date'));
    
    console.log('[F12 Paste] 🔍 Checking DatePicker - placeholder:', placeholder, 'isDatePicker:', isDatePickerByPlaceholder);
    
    // Secondary detection: Try to find DatePicker root in DOM
    let datePickerRoot = actualInput.closest('.MuiDatePicker-root') || 
                         element.closest('.MuiDatePicker-root') ||
                         actualInput.closest('[class*="DatePicker"]') ||
                         element.closest('[class*="DatePicker"]');
    
    // Also check parent elements more thoroughly
    if (!datePickerRoot) {
        let parent = actualInput.parentElement;
        let depth = 0;
        while (parent && depth < 10) {
            if (parent.classList && (
                parent.classList.contains('MuiDatePicker-root') ||
                Array.from(parent.classList).some(cls => cls.includes('DatePicker'))
            )) {
                datePickerRoot = parent;
                break;
            }
            parent = parent.parentElement;
            depth++;
        }
    }
    
    console.log('[F12 Paste] DatePicker detection - root:', datePickerRoot, 'by placeholder:', isDatePickerByPlaceholder, 'placeholder:', actualInput.placeholder);
    
    // Use placeholder detection as primary method (most reliable)
    // If placeholder is "DD/MM/YYYY", treat it as DatePicker even if root not found
    if (isDatePickerByPlaceholder || datePickerRoot) {
        console.log('[F12 Paste] ✅ Found MUI DatePicker, setting value:', value);
        const dateInput = datePickerRoot ? (datePickerRoot.querySelector('input') || actualInput) : actualInput;
        if (dateInput) {
            // Focus the input first
            dateInput.focus();
            dateInput.select();
            
            // Method 1: Try using execCommand('insertText') which often works better with React
            try {
                // Clear first
                dateInput.setSelectionRange(0, dateInput.value.length);
                document.execCommand('delete', false);
                
                // Set selection to start
                dateInput.setSelectionRange(0, 0);
                
                // Use insertText command
                if (document.execCommand('insertText', false, value)) {
                    console.log('[F12 Paste] ✅ Used execCommand insertText for DatePicker');
                    
                    // Trigger input event
                    const inputEvent = new InputEvent('input', {
                        bubbles: true,
                        cancelable: true,
                        inputType: 'insertFromPaste',
                        data: value
                    });
                    dateInput.dispatchEvent(inputEvent);
                    
                    // Wait for debounce (300ms) then verify
                    setTimeout(() => {
                        const finalValue = dateInput.value;
                        if (finalValue && finalValue !== 'DD/MM/YYYY' && finalValue !== dateInput.placeholder) {
                            console.log('[F12 Paste] ✅ DatePicker value set via execCommand:', finalValue);
                        } else {
                            console.log('[F12 Paste] ⚠️ DatePicker value reset, trying character-by-character method');
                            // Fallback to character-by-character
                            typeDateCharacterByCharacter(dateInput, value);
                        }
                    }, 400);
                    
                    return; // Return early
                }
            } catch (e) {
                console.log('[F12 Paste] execCommand failed, using character-by-character:', e);
            }
            
            // Method 2: Type character by character (fallback)
            typeDateCharacterByCharacter(dateInput, value);
            console.log('[F12 Paste] ✅ DatePicker paste initiated, returning early');
            return; // IMPORTANT: Return early to skip regular input handling
        } else {
            console.log('[F12 Paste] ⚠️ DatePicker detected but dateInput not found');
        }
    } else {
        console.log('[F12 Paste] ℹ️ Not a DatePicker - isDatePickerByPlaceholder:', isDatePickerByPlaceholder, 'datePickerRoot:', datePickerRoot);
    }
    
    const tagName = actualInput.tagName?.toLowerCase();
    console.log('[F12 Paste] Input tag name:', tagName);
    
    // Use native setter to bypass React's controlled component restrictions
    if (tagName === 'select') {
        console.log('[F12 Paste] Setting select value');
        // For select, try to find option by value first
        const optionByValue = Array.from(actualInput.options).find(opt => opt.value === value);
        if (optionByValue) {
            actualInput.selectedIndex = optionByValue.index;
            console.log('[F12 Paste] Selected option by value:', value);
        } else {
            // Try to find by text content
            const optionByText = Array.from(actualInput.options).find(opt => 
                opt.textContent?.trim() === value || opt.text?.trim() === value
            );
            if (optionByText) {
                actualInput.selectedIndex = optionByText.index;
                console.log('[F12 Paste] Selected option by text:', value);
            } else {
                // Direct value assignment
                actualInput.value = value;
                console.log('[F12 Paste] Set select value directly:', value);
            }
        }
        console.log('[F12 Paste] Value after set (select):', actualInput.value);
    } else if (tagName === 'input') {
        const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
        if (setter) {
            console.log('[F12 Paste] Using native setter for input');
            setter.call(actualInput, value);
        } else {
            console.log('[F12 Paste] Using direct value assignment for input');
            actualInput.value = value;
        }
        console.log('[F12 Paste] Value after set (input):', actualInput.value);
    } else if (tagName === 'textarea') {
        const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;
        if (setter) {
            console.log('[F12 Paste] Using native setter for textarea');
            setter.call(actualInput, value);
        } else {
            console.log('[F12 Paste] Using direct value assignment for textarea');
            actualInput.value = value;
        }
        console.log('[F12 Paste] Value after set (textarea):', actualInput.value);
    } else if (actualInput.contentEditable === 'true') {
        console.log('[F12 Paste] Setting contenteditable textContent');
        actualInput.textContent = value;
        console.log('[F12 Paste] Value after set (contenteditable):', actualInput.textContent);
    } else {
        // For MUI Select (non-native), try to trigger change via click/input
        const muiSelectRoot = element.closest('.MuiSelect-root, .MuiSelect-select');
        if (muiSelectRoot) {
            console.log('[F12 Paste] Setting MUI Select value');
            // Try to find and click the option
            const selectMenu = document.querySelector('.MuiMenu-root, .MuiPopover-root');
            if (selectMenu) {
                const option = Array.from(selectMenu.querySelectorAll('[role="option"], .MuiMenuItem-root')).find(opt => 
                    opt.textContent?.trim() === value || opt.getAttribute('data-value') === value
                );
                if (option) {
                    option.click();
                    console.log('[F12 Paste] Clicked MUI Select option');
                }
            }
            // Also set value on hidden input if exists
            const hiddenInput = muiSelectRoot.querySelector('input[type="hidden"]');
            if (hiddenInput) {
                hiddenInput.value = value;
            }
        }
    }
    
    // Dispatch events to trigger React state updates
    console.log('[F12 Paste] Dispatching input event');
    const inputEvent = new Event('input', { bubbles: true, cancelable: true });
    actualInput.dispatchEvent(inputEvent);
    console.log('[F12 Paste] Input event dispatched');
    
    console.log('[F12 Paste] Dispatching change event');
    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    actualInput.dispatchEvent(changeEvent);
    console.log('[F12 Paste] Change event dispatched');
    
    // For select elements, also dispatch a select event
    if (tagName === 'select') {
        console.log('[F12 Paste] Dispatching select event');
        const selectEvent = new Event('select', { bubbles: true, cancelable: true });
        actualInput.dispatchEvent(selectEvent);
    }
    
    // For MUI Select, trigger click to open and select
    const muiSelectRoot = element.closest('.MuiSelect-root, .MuiSelect-select');
    if (muiSelectRoot && tagName !== 'select') {
        console.log('[F12 Paste] Found MUI Select, triggering click');
        // Click to open the select menu
        muiSelectRoot.click();
        // Wait a bit for menu to open, then find and click the option
        setTimeout(() => {
            const selectMenu = document.querySelector('.MuiMenu-root[role="listbox"], .MuiPopover-root[role="listbox"]');
            if (selectMenu) {
                const option = Array.from(selectMenu.querySelectorAll('[role="option"], .MuiMenuItem-root')).find(opt => 
                    opt.textContent?.trim() === value || opt.getAttribute('data-value') === value || opt.getAttribute('value') === value
                );
                if (option) {
                    option.click();
                    console.log('[F12 Paste] Clicked MUI Select option');
                }
            }
        }, 100);
    }
    
    // For MUI Autocomplete, set value and trigger selection
    const autocompleteRoot = element.closest('.MuiAutocomplete-root');
    if (autocompleteRoot) {
        console.log('[F12 Paste] Found MUI Autocomplete, setting value (name):', value);
        console.log('[F12 Paste] Value type:', typeof value, 'Length:', value?.length);
        
        // Normalize the value: trim and remove extra spaces
        const normalizeValue = (val) => {
            if (!val) return '';
            return String(val).trim().replace(/\s+/g, ' ');
        };
        
        // Ensure value is just the name, not the full display string
        // If value contains " - ", extract just the name part
        let nameToSearch = normalizeValue(value);
        if (nameToSearch && nameToSearch.includes(' - ')) {
            const parts = nameToSearch.split(' - ');
            if (parts.length >= 2) {
                nameToSearch = normalizeValue(parts[1]);
                console.log('[F12 Paste] Extracted name from value:', nameToSearch, 'from:', value);
            }
        }
        
        // Strategy: Type the name to filter, then click the matching option
        actualInput.focus();
        
        // Clear current value first
        actualInput.value = '';
        const clearEvent = new Event('input', { bubbles: true, cancelable: true });
        actualInput.dispatchEvent(clearEvent);
        
        // Type ONLY the name (not the full display string) to filter options
        actualInput.value = nameToSearch;
        const inputEvent = new Event('input', { bubbles: true, cancelable: true });
        actualInput.dispatchEvent(inputEvent);
        
        console.log('[F12 Paste] Typed normalized name into input:', nameToSearch);
        
        // Small delay to let filtering happen, then find and click matching option
        setTimeout(() => {
            const optionsList = document.querySelector('.MuiAutocomplete-listbox, .MuiAutocomplete-popper');
            if (optionsList) {
                const options = Array.from(optionsList.querySelectorAll('[role="option"], .MuiAutocomplete-option'));
                console.log('[F12 Paste] Found', options.length, 'options in listbox');
                
                // Find option that contains the name in its text
                // Options are displayed as "ID - Name - AccountCaseId" or "ID - Name"
                const matchedOption = options.find(option => {
                    const optionText = option.textContent || '';
                    const normalizedOptionText = normalizeValue(optionText);
                    
                    // Extract name from option display string
                    const parts = normalizedOptionText.split(' - ');
                    const nameInOption = parts.length >= 2 ? normalizeValue(parts[1]) : normalizedOptionText;
                    
                    // Normalize both for comparison
                    const normalizedSearch = normalizeValue(nameToSearch);
                    const normalizedOptionName = normalizeValue(nameInOption);
                    
                    // Match by name (exact match preferred, then contains)
                    const exactMatch = normalizedOptionName === normalizedSearch;
                    const containsMatch = normalizedOptionName.includes(normalizedSearch) || 
                                        normalizedSearch.includes(normalizedOptionName);
                    
                    if (exactMatch || containsMatch) {
                        console.log('[F12 Paste] Found matching option:', optionText, 'name:', nameInOption, 'search:', nameToSearch);
                        return true;
                    }
                    return false;
                });
                
                if (matchedOption) {
                    // Click the matched option
                    matchedOption.click();
                    console.log('[F12 Paste] ✅ Clicked Autocomplete option:', matchedOption.textContent?.trim());
                    return;
                } else {
                    console.log('[F12 Paste] No exact match found, trying first filtered option');
                }
            }
            
            // If no exact match, try clicking first filtered option
            setTimeout(() => {
                const filteredOptions = document.querySelectorAll('.MuiAutocomplete-listbox [role="option"]');
                if (filteredOptions.length > 0) {
                    filteredOptions[0].click();
                    console.log('[F12 Paste] ✅ Clicked first filtered option:', filteredOptions[0].textContent?.trim());
                } else {
                    console.log('[F12 Paste] No filtered options found');
                    // If still no match, dispatch change event
                    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
                    actualInput.dispatchEvent(changeEvent);
                }
            }, 100);
        }, 150);
        
        console.log('[F12 Paste] Autocomplete paste initiated');
        return; // Return early to skip regular input handling
    }
    
    // Verify the value was set
    const finalValue = tagName === 'input' || tagName === 'textarea' ? actualInput.value : actualInput.textContent;
    console.log('[F12 Paste] Final value stored in field:', finalValue);
    console.log('[F12 Paste] Value successfully stored!');
}

/**
 * Main handler for F12 key press
 */
function handleF12KeyPress(event) {
    // Only handle F12
    if (event.key !== 'F12' && event.keyCode !== 123) {
        return;
    }
    
    console.log('[F12 Paste] ========== F12 KEY PRESSED ==========');
    console.log('[F12 Paste] Event:', event);
    
    // Prevent default (opening DevTools) and stop propagation
    event.preventDefault();
    event.stopPropagation();
    console.log('[F12 Paste] Prevented default and stopped propagation');
    
    // Get currently focused element
    const focusedElement = document.activeElement;
    if (!focusedElement) {
        console.log('[F12 Paste] ❌ No focused element');
        return;
    }
    console.log('[F12 Paste] ✅ Focused element found:', focusedElement, 'Tag:', focusedElement.tagName, 'Type:', focusedElement.type);
    
    // Check if it's an editable field
    if (!isEditableField(focusedElement)) {
        console.log('[F12 Paste] ❌ Not an editable field:', focusedElement);
        return;
    }
    console.log('[F12 Paste] ✅ Field is editable');
    
    // Find the row container (optional - can work without it using stored values)
    const rowContainer = findRowContainer(focusedElement);
    
    // Create signature for current field FIRST (needed for both previous row and storage lookup)
    let signature = null;
    if (rowContainer) {
        signature = createFieldSignature(focusedElement, rowContainer);
    } else {
        // Fallback: create a simple signature without row container
        const actualInput = getActualInputElement(focusedElement);
        if (actualInput) {
            // Use placeholder, name, or id as signature
            if (actualInput.placeholder) {
                signature = { type: 'placeholder', value: actualInput.placeholder };
            } else if (actualInput.name) {
                signature = { type: 'name', value: actualInput.name };
            } else if (actualInput.id) {
                signature = { type: 'id', value: actualInput.id };
            }
        }
    }
    
    if (!signature) {
        console.log('[F12 Paste] ❌ Could not create field signature');
        return;
    }
    console.log('[F12 Paste] ✅ Field signature created:', signature);
    
    // If we have a row container, try to find previous row
    let previousRow = null;
    if (rowContainer) {
        console.log('[F12 Paste] ✅ Row container found:', rowContainer, 'Tag:', rowContainer.tagName, 'Classes:', rowContainer.className);
        previousRow = findPreviousRow(rowContainer);
    } else {
        console.log('[F12 Paste] ⚠️ No row container found - will use stored values only');
    }
    
    if (!previousRow) {
        console.log('[F12 Paste] ⚠️ No previous row found - checking stored values...');
        
        // Try to get stored value for this field signature
        const storageKey = getStorageKey(signature);
        console.log('[F12 Paste] Looking for storage key:', storageKey);
        console.log('[F12 Paste] All stored keys:', Array.from(fieldValueStorage.keys()));
        console.log('[F12 Paste] Storage size:', fieldValueStorage.size);
        
        if (storageKey && fieldValueStorage.has(storageKey)) {
            const storedValue = fieldValueStorage.get(storageKey);
            console.log('[F12 Paste] ✅ Found stored value for', storageKey, ':', storedValue);
            setFieldValue(focusedElement, storedValue);
            console.log('[F12 Paste] ========== F12 PASTE COMPLETE (from storage) ==========');
            return;
        } else {
            console.log('[F12 Paste] ❌ No previous row and no stored value found');
            console.log('[F12 Paste] Storage key searched:', storageKey);
            console.log('[F12 Paste] Storage has key?', storageKey ? fieldValueStorage.has(storageKey) : 'no key');
            
            // Try to find by partial match (in case placeholder text differs slightly)
            if (storageKey) {
                const matchingKeys = Array.from(fieldValueStorage.keys()).filter(key => {
                    // Check if the key contains the same placeholder value
                    const keyParts = key.split(':');
                    const sigParts = storageKey.split(':');
                    if (keyParts.length === 2 && sigParts.length === 2) {
                        return keyParts[1] === sigParts[1]; // Match by value part
                    }
                    return false;
                });
                
                if (matchingKeys.length > 0) {
                    console.log('[F12 Paste] Found partial match:', matchingKeys);
                    const storedValue = fieldValueStorage.get(matchingKeys[0]);
                    console.log('[F12 Paste] ✅ Using partial match value:', storedValue);
                    setFieldValue(focusedElement, storedValue);
                    console.log('[F12 Paste] ========== F12 PASTE COMPLETE (from storage - partial match) ==========');
                    return;
                }
            }
            
            if (rowContainer) {
                const parent = rowContainer.parentElement;
                if (parent) {
                    const siblings = Array.from(parent.children);
                    console.log('[F12 Paste] Siblings count:', siblings.length, 'Current index:', siblings.indexOf(rowContainer));
                }
            }
            return;
        }
    }
    
    console.log('[F12 Paste] ✅ Previous row found:', previousRow, 'Tag:', previousRow.tagName);
    console.log('[F12 Paste] ✅ Previous row found:', previousRow, 'Tag:', previousRow.tagName);
    
    // Find matching field in previous row
    console.log('[F12 Paste] Searching for matching field in previous row...');
    console.log('[F12 Paste] Previous row HTML:', previousRow.outerHTML.substring(0, 200));
    const allInputsInPreviousRow = previousRow.querySelectorAll('input, textarea, [contenteditable="true"]');
    console.log('[F12 Paste] All inputs found in previous row:', allInputsInPreviousRow.length);
    allInputsInPreviousRow.forEach((input, idx) => {
        console.log(`[F12 Paste]   Input ${idx}:`, input.tagName, 'id:', input.id, 'name:', input.name, 'placeholder:', input.placeholder, 'value:', input.value);
    });
    
    const previousField = findMatchingField(previousRow, signature);
    if (!previousField) {
        console.log('[F12 Paste] ❌ Could not find matching field in previous row');
        console.log('[F12 Paste] Signature used:', signature);
        console.log('[F12 Paste] Available inputs in previous row:', allInputsInPreviousRow.length);
        
        // Try to find by path as fallback
        console.log('[F12 Paste] Attempting path-based matching as fallback...');
        const signatureByPath = createFieldSignature(focusedElement, rowContainer);
        if (signatureByPath && signatureByPath.type === 'path') {
            console.log('[F12 Paste] Trying path signature:', signatureByPath);
            const previousFieldByPath = findMatchingField(previousRow, signatureByPath);
            if (previousFieldByPath) {
                console.log('[F12 Paste] ✅ Found field using path signature!');
                const value = extractFieldValue(previousFieldByPath);
                if (value !== null && value !== undefined && value !== '') {
                    setFieldValue(focusedElement, value);
                    console.log('[F12 Paste] ========== F12 PASTE COMPLETE ==========');
                    return;
                }
            }
        }
        return;
    }
    console.log('[F12 Paste] ✅ Matching field found in previous row:', previousField);
    
    // Extract value from previous field
    console.log('[F12 Paste] 📥 Extracting value from previous field...');
    const value = extractFieldValue(previousField);
    if (value === null || value === undefined || value === '') {
        console.log('[F12 Paste] ⚠️ Previous field has no value - checking stored values...');
        
        // Try to get stored value as fallback
        const storageKey = getStorageKey(signature);
        if (storageKey && fieldValueStorage.has(storageKey)) {
            const storedValue = fieldValueStorage.get(storageKey);
            console.log('[F12 Paste] ✅ Found stored value for', storageKey, ':', storedValue);
            setFieldValue(focusedElement, storedValue);
            console.log('[F12 Paste] ========== F12 PASTE COMPLETE (from storage) ==========');
            return;
        } else {
            console.log('[F12 Paste] ❌ Previous field has no value and no stored value found');
            return;
        }
    }
    console.log('[F12 Paste] ✅ Value extracted:', value, 'Type:', typeof value, 'Length:', value.length);
    
    // Store this value for future use (async, non-blocking)
    const storageKey = getStorageKey(signature);
    if (storageKey) {
        // Use requestIdleCallback for non-blocking storage
        if (window.requestIdleCallback) {
            window.requestIdleCallback(() => {
                fieldValueStorage.set(storageKey, value);
                if (process.env.NODE_ENV === 'development') {
                    console.log('[F12 Paste] 💾 Stored extracted value for future use:', storageKey, ':', value);
                }
            }, { timeout: 50 });
        } else {
            setTimeout(() => {
                fieldValueStorage.set(storageKey, value);
                if (process.env.NODE_ENV === 'development') {
                    console.log('[F12 Paste] 💾 Stored extracted value for future use:', storageKey, ':', value);
                }
            }, 0);
        }
    }
    
    // Set value in current field
    console.log('[F12 Paste] 📤 Storing value in current field...');
    setFieldValue(focusedElement, value);
    console.log('[F12 Paste] ========== F12 PASTE COMPLETE ==========');
}

/**
 * Stores a value for a given element (extracted to be reusable)
 * Optimized for performance - uses async storage in background
 */
function storeValueForElement(element, value) {
    if (!element || !value) return;
    
    // Use requestIdleCallback for non-blocking storage (runs in background)
    const store = () => {
        // Find the row container to create a signature (optional - can work without it)
        const rowContainer = findRowContainer(element);
        
        // Create signature for this field (with or without row container)
        let signature = null;
        if (rowContainer) {
            signature = createFieldSignature(element, rowContainer);
        } else {
            // Fallback: create a simple signature without row container
            const actualInput = getActualInputElement(element);
            if (actualInput) {
                // Use placeholder, name, or id as signature
                if (actualInput.placeholder) {
                    signature = { type: 'placeholder', value: actualInput.placeholder };
                } else if (actualInput.name) {
                    signature = { type: 'name', value: actualInput.name };
                } else if (actualInput.id) {
                    signature = { type: 'id', value: actualInput.id };
                }
            }
        }
        
        if (!signature) {
            return; // Silently skip if no signature
        }
        
        // Store the value (fast in-memory, async persistence)
        const storageKey = getStorageKey(signature);
        if (storageKey && value) {
            fieldValueStorage.set(storageKey, value);
            // Log only in development
            if (process.env.NODE_ENV === 'development') {
                console.log('[F12 Paste] 💾 Stored value for', storageKey, ':', value);
            }
        }
    };
    
    // Use requestIdleCallback for background processing (non-blocking)
    if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(store, { timeout: 100 });
    } else {
        // Fallback: use setTimeout(0) for async execution
        setTimeout(store, 0);
    }
}

/**
 * Handles input/change events to store field values
 * Uses async/non-blocking approach to avoid performance impact
 */
function handleInputChange(event) {
    // Store immediately (synchronously) so it's available right away
    try {
        const target = event.target;
        if (!target) return;
        
        const tagName = target.tagName?.toLowerCase();
        
        // Handle input, textarea, and select elements
        if (tagName !== 'input' && tagName !== 'textarea' && tagName !== 'select') {
            // Check if it's inside a MUI Select or date picker
            const muiSelect = target.closest('.MuiSelect-root, .MuiSelect-select');
            const datePicker = target.closest('.react-datepicker-wrapper, .MuiDatePicker-root, .MuiDatePicker-input');
            if (!muiSelect && !datePicker) return;
        }
        
        // Skip if disabled/readonly/password
        if (target.disabled || target.readOnly || target.type === 'password') return;
        
        // Get the value
        let value = '';
        if (tagName === 'select') {
            value = target.value || '';
            // Also try to get the selected option text
            if (target.selectedOptions && target.selectedOptions.length > 0) {
                const selectedText = target.selectedOptions[0].textContent?.trim();
                // Store both value and text for better matching
                if (selectedText && selectedText !== value) {
                    value = selectedText; // Prefer text for better matching
                }
            }
        } else {
            value = target.value || '';
        }
        
        // For date pickers (MUI DatePicker), get the formatted date from input
        const datePickerRoot = target.closest('.MuiDatePicker-root');
        if (datePickerRoot) {
            const datePickerInput = datePickerRoot.querySelector('input') || target;
            if (datePickerInput && datePickerInput.value) {
                const dateValue = datePickerInput.value.trim();
                // Only store if it's a valid date format (not placeholder)
                if (dateValue && dateValue !== 'DD/MM/YYYY' && dateValue !== datePickerInput.placeholder) {
                    value = dateValue;
                    console.log('[F12 Paste] 💾 Date picker input value detected:', value);
                }
            }
        } else if (target.type === 'date' || target.closest('.react-datepicker-wrapper')) {
            value = target.value || '';
        }
        
        // For MUI Autocomplete, extract the name part from the display string
        const autocompleteRoot = target.closest('.MuiAutocomplete-root');
        if (autocompleteRoot) {
            const autocompleteInput = autocompleteRoot.querySelector('input');
            if (autocompleteInput && autocompleteInput.value) {
                const inputDisplay = autocompleteInput.value.trim();
                
                // Extract the name part from formats like:
                // "92 - בדרדקהה - 208219212" -> extract "בדרדקהה"
                // "65 - מע"מ חו"ז" -> extract "מע"מ חו"ז"
                // Normalize: trim and remove extra spaces
                const normalizedDisplay = inputDisplay.replace(/\s+/g, ' ').trim();
                const parts = normalizedDisplay.split(' - ');
                if (parts.length >= 2) {
                    // Always get the second part (index 1) which is the name
                    // Normalize: trim and remove extra spaces
                    value = parts[1].trim().replace(/\s+/g, ' ');
                    console.log('[F12 Paste] 💾 Stored Autocomplete name:', value, 'from display:', inputDisplay);
                } else {
                    // If no separator, use the whole value (normalized)
                    value = normalizedDisplay;
                    console.log('[F12 Paste] 💾 Stored Autocomplete value (no separator):', value);
                }
            }
        }
        
        // Filter out placeholder values dynamically
        // Get placeholder from the actual input element (handles MUI components)
        const actualInput = getActualInputElement(target);
        const placeholderText = (actualInput?.placeholder || target.placeholder || '').trim();
        const valueTrimmed = String(value).trim();
        
        // Check if value matches placeholder (case-insensitive for better matching)
        const matchesPlaceholder = placeholderText && 
                                   valueTrimmed.toLowerCase() === placeholderText.toLowerCase();
        
        // Check if value is empty or just whitespace
        const isEmpty = !valueTrimmed || valueTrimmed === '';
        
        // Check if value looks like a placeholder (common patterns)
        // This catches cases where placeholder might be set via CSS or other means
        const looksLikePlaceholder = valueTrimmed && (
            valueTrimmed.match(/^(DD|MM|YYYY|dd|mm|yyyy)[\/\-]/) || // Date patterns
            valueTrimmed === target.getAttribute('placeholder') ||
            valueTrimmed === target.getAttribute('data-placeholder')
        );
        
        if (matchesPlaceholder || isEmpty || looksLikePlaceholder) {
            console.log('[F12 Paste] ⏭️ Skipping storage - placeholder or empty value:', {
                value: valueTrimmed,
                placeholder: placeholderText,
                matchesPlaceholder,
                isEmpty,
                looksLikePlaceholder
            });
            return;
        }
        
        console.log('[F12 Paste] ✅ Value is valid for storage:', valueTrimmed);
        
        // Store the value
        storeValueForElement(target, valueTrimmed);
    } catch (error) {
        // Silently handle errors to avoid breaking the app
        console.error('[F12 Paste] Error storing value:', error);
    }
}

/**
 * Observes DOM changes to capture Autocomplete and DatePicker values
 * These components don't always trigger standard input/change events
 * Optimized for performance - only observes relevant changes
 */
function setupMutationObserver() {
    let lastStoredValue = new Map(); // Track last stored values to avoid duplicates
    
    const observer = new MutationObserver((mutations) => {
        // Process mutations synchronously for speed
        for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.target) {
                const target = mutation.target;
                if (target.tagName === 'INPUT' && mutation.attributeName === 'value' && target.value) {
                    const value = target.value.trim();
                    if (!value || value === target.placeholder || value === 'DD/MM/YYYY') continue;
                    
                    const autocompleteRoot = target.closest('.MuiAutocomplete-root');
                    const datePickerRoot = target.closest('.MuiDatePicker-root');
                    
                    if (autocompleteRoot || datePickerRoot) {
                        // Check if we already stored this value recently
                        const storageKey = `${target.id || target.placeholder || 'unknown'}:${value}`;
                        if (lastStoredValue.get(storageKey) === value) continue;
                        
                        // Get actual value for Autocomplete
                        let actualValue = value;
                        if (autocompleteRoot) {
                            const selectedOption = autocompleteRoot.querySelector('[aria-selected="true"], .MuiAutocomplete-tag');
                            if (selectedOption) {
                                const optionValue = selectedOption.getAttribute('data-value') || 
                                                  selectedOption.getAttribute('data-option-value');
                                if (optionValue) actualValue = optionValue;
                            }
                        }
                        
                        // For date pickers, ensure we store the formatted date
                        if (datePickerRoot) {
                            console.log('[F12 Paste] 📅 MutationObserver detected date picker value change:', value);
                            // Give React a moment to fully update
                            setTimeout(() => {
                                const finalValue = target.value?.trim() || '';
                                if (finalValue && finalValue !== 'DD/MM/YYYY' && finalValue !== target.placeholder) {
                                    storeValueForElement(target, finalValue);
                                    lastStoredValue.set(storageKey, finalValue);
                                }
                            }, 100);
                        } else {
                            storeValueForElement(target, actualValue);
                            lastStoredValue.set(storageKey, actualValue);
                        }
                        
                        // Clean up old entries (keep last 100)
                        if (lastStoredValue.size > 100) {
                            const firstKey = lastStoredValue.keys().next().value;
                            lastStoredValue.delete(firstKey);
                        }
                    }
                }
            }
            
            // Check for date picker calendar selections
            if (mutation.type === 'childList') {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== 1) continue;
                    
                    // Check if a date was selected from calendar
                    const datePickerDay = node.querySelector?.('.MuiPickersDay-selected, .MuiDayCalendar-daySelected, [role="gridcell"][aria-selected="true"], .MuiPickersDay-root[aria-selected="true"]');
                    if (datePickerDay) {
                        const datePickerRoot = datePickerDay.closest('.MuiDatePicker-root');
                        if (datePickerRoot) {
                            const dateInput = datePickerRoot.querySelector('input');
                            if (dateInput) {
                                // Wait for React to update the input value after date selection
                                setTimeout(() => {
                                    const value = dateInput.value?.trim() || '';
                                    if (value && value !== 'DD/MM/YYYY' && value !== dateInput.placeholder && value.length > 0) {
                                        console.log('[F12 Paste] 📅 Date selected from calendar:', value);
                                        storeValueForElement(dateInput, value);
                                    }
                                }, 200); // Give React time to update
                            }
                        }
                    }
                }
            }
            
            // Also check for date picker popup closing (indicates date was selected)
            if (mutation.type === 'childList') {
                for (const removedNode of mutation.removedNodes) {
                    if (removedNode.nodeType === 1) {
                        // Check if date picker popup/popper was removed (calendar closed)
                        const isDatePickerPopper = removedNode.classList?.contains('MuiPopper-root') ||
                                                   removedNode.querySelector?.('.MuiDatePicker-root, .MuiPickersPopper-root');
                        if (isDatePickerPopper) {
                            // Find the date input that was associated with this picker
                            const dateInputs = document.querySelectorAll('.MuiDatePicker-root input');
                            dateInputs.forEach(dateInput => {
                                setTimeout(() => {
                                    const value = dateInput.value?.trim() || '';
                                    if (value && value !== 'DD/MM/YYYY' && value !== dateInput.placeholder && value.length > 0) {
                                        console.log('[F12 Paste] 📅 Date picker closed, storing value:', value);
                                        storeValueForElement(dateInput, value);
                                    }
                                }, 100);
                            });
                        }
                    }
                }
            }
        }
    });
    
    // Observe only relevant parts - more targeted for performance
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'aria-selected']
    });
    
    return observer;
}

/**
 * Installs the global F12 paste from previous row feature
 * 
 * @returns {Function} Uninstall function to remove the event listener
 */
export function installF12PasteFromPreviousRow() {
    // Add global keydown listener for F12
    window.addEventListener('keydown', handleF12KeyPress, true); // Use capture phase
    
    // Add global input/change listeners to capture values as user types
    // Use capture phase to catch events early
    window.addEventListener('input', handleInputChange, true);
    window.addEventListener('change', handleInputChange, true);
    
    // Set up mutation observer to catch Autocomplete and DatePicker changes
    const mutationObserver = setupMutationObserver();
    
    // Track if date picker popper was open (to detect when it closes)
    let wasDatePickerOpen = false;
    let lastFocusedDateInput = null;
    let datePickerCloseTimeout = null;
    
    const checkDatePickerState = () => {
        const isOpen = !!document.querySelector('.MuiPickersPopper-root, .MuiPopper-root[role="dialog"], .MuiPopover-root[role="dialog"]');
        if (wasDatePickerOpen && !isOpen) {
            // Date picker just closed, store the value
            console.log('[F12 Paste] 📅 Date picker closed, checking for values...');
            
            // Clear any existing timeout
            if (datePickerCloseTimeout) {
                clearTimeout(datePickerCloseTimeout);
            }
            
            // Try multiple times with increasing delays to catch the value after React updates
            const tryStoreDate = (attempt = 1) => {
                datePickerCloseTimeout = setTimeout(() => {
                    // First try the last focused date input (most reliable)
                    if (lastFocusedDateInput) {
                        const value = lastFocusedDateInput.value?.trim() || '';
                        if (value && value !== 'DD/MM/YYYY' && value !== lastFocusedDateInput.placeholder && value.length > 0) {
                            console.log('[F12 Paste] 📅 Storing from last focused input:', value);
                            storeValueForElement(lastFocusedDateInput, value);
                            lastFocusedDateInput = null;
                            return;
                        } else if (attempt < 3) {
                            console.log('[F12 Paste] 📅 Date value not ready yet (attempt', attempt, '), retrying...');
                            tryStoreDate(attempt + 1);
                            return;
                        }
                    }
                    
                    // Otherwise check all date inputs
                    const dateInputs = document.querySelectorAll('.MuiDatePicker-root input');
                    console.log('[F12 Paste] 📅 Checking', dateInputs.length, 'date inputs');
                    dateInputs.forEach(dateInput => {
                        const value = dateInput.value?.trim() || '';
                        if (value && value !== 'DD/MM/YYYY' && value !== dateInput.placeholder && value.length > 0) {
                            console.log('[F12 Paste] 📅 Date picker closed, storing:', value);
                            storeValueForElement(dateInput, value);
                        }
                    });
                }, attempt * 200); // 200ms, 400ms, 600ms
            };
            
            tryStoreDate();
        }
        wasDatePickerOpen = isOpen;
    };
    
    // Listen for click events on date picker calendar days to capture date selections
    const handleDatePickerDayClick = (event) => {
        const clickedElement = event.target;
        // Check if a calendar day was clicked (various possible class names and structures)
        const isDateDay = clickedElement.classList?.contains('MuiPickersDay-root') ||
                         clickedElement.classList?.contains('MuiDayCalendar-day') ||
                         clickedElement.closest('.MuiPickersDay-root') ||
                         clickedElement.closest('[role="gridcell"]') ||
                         clickedElement.getAttribute('role') === 'gridcell' ||
                         clickedElement.closest('[role="gridcell"]') ||
                         (clickedElement.textContent && /^\d+$/.test(clickedElement.textContent.trim()) && 
                          clickedElement.closest('.MuiPickersCalendar-root, .MuiDayCalendar-root'));
        
        if (isDateDay) {
            console.log('[F12 Paste] 📅 Calendar day clicked, element:', clickedElement);
            
            // Find the date picker root (could be in various locations)
            let datePickerRoot = clickedElement.closest('.MuiDatePicker-root');
            if (!datePickerRoot) {
                // Try to find it by looking for the popper/popup
                const popper = clickedElement.closest('.MuiPickersPopper-root, .MuiPopper-root, .MuiPopover-root');
                if (popper) {
                    console.log('[F12 Paste] 📅 Found popper, searching for associated date input...');
                    // First, try to use the last focused date input (most reliable)
                    if (lastFocusedDateInput) {
                        datePickerRoot = lastFocusedDateInput.closest('.MuiDatePicker-root');
                        if (datePickerRoot) {
                            console.log('[F12 Paste] 📅 Found date picker root via lastFocusedDateInput');
                        }
                    }
                    
                    // If still not found, check all date inputs
                    if (!datePickerRoot) {
                        const allDateInputs = document.querySelectorAll('.MuiDatePicker-root input');
                        console.log('[F12 Paste] 📅 Checking', allDateInputs.length, 'date inputs');
                        // The input that was focused before the calendar opened is likely the one
                        for (const input of allDateInputs) {
                            const root = input.closest('.MuiDatePicker-root');
                            if (root) {
                                // Check if there's an open popper near this root
                                const rootPopper = root.querySelector('.MuiPickersPopper-root, .MuiPopper-root');
                                if (rootPopper || lastFocusedDateInput === input) {
                                    datePickerRoot = root;
                                    console.log('[F12 Paste] 📅 Found associated date picker root');
                                    break;
                                }
                            }
                        }
                    }
                }
            }
            
            if (datePickerRoot) {
                const dateInput = datePickerRoot.querySelector('input');
                if (dateInput) {
                    console.log('[F12 Paste] 📅 Date day clicked, waiting for React update...');
                    // Wait for React to update the input after date selection
                    // Try multiple times with increasing delays to catch the value
                    const checkAndStore = (attempt = 1) => {
                        setTimeout(() => {
                            const value = dateInput.value?.trim() || '';
                            if (value && value !== 'DD/MM/YYYY' && value !== dateInput.placeholder && value.length > 0) {
                                console.log('[F12 Paste] 📅 Date day clicked, storing:', value);
                                storeValueForElement(dateInput, value);
                            } else if (attempt < 3) {
                                // Try again if value not ready yet
                                console.log('[F12 Paste] 📅 Date value not ready yet (attempt', attempt, '), value:', value);
                                checkAndStore(attempt + 1);
                            } else {
                                console.log('[F12 Paste] 📅 Date value not found after 3 attempts');
                            }
                        }, attempt * 200); // 200ms, 400ms, 600ms
                    };
                    checkAndStore();
                } else {
                    console.log('[F12 Paste] 📅 Date picker root found but no input element');
                }
            } else {
                console.log('[F12 Paste] 📅 Date day clicked but could not find date picker root');
            }
        }
    };
    
    // Listen for when date picker popup closes (calendar closes after selection)
    const handleDocumentClick = (event) => {
        // Update state before checking
        checkDatePickerState();
    };
    
    // Monitor date picker state via mutation observer
    const datePickerStateObserver = new MutationObserver(() => {
        checkDatePickerState();
    });
    datePickerStateObserver.observe(document.body, {
        childList: true,
        subtree: true
    });
    
    // Add event listeners
    document.addEventListener('click', handleDatePickerDayClick, true);
    document.addEventListener('click', handleDocumentClick, true);
    
    // Also listen for focus events on Autocomplete/DatePicker to capture their values
    // Optimized - uses requestAnimationFrame instead of setTimeout for faster execution
    const handleFocus = (event) => {
        const target = event.target;
        if (!target) return;
        
        // Ensure target is an Element (has closest method)
        let element = target;
        if (typeof target.closest !== 'function') {
            if (target.parentElement && typeof target.parentElement.closest === 'function') {
                element = target.parentElement;
            } else {
                return;
            }
        }
        
        // Check if it's an Autocomplete or DatePicker input
        const autocompleteRoot = element.closest('.MuiAutocomplete-root');
        const datePickerRoot = element.closest('.MuiDatePicker-root');
        
        if (datePickerRoot) {
            // Track the focused date input for later use
            const dateInput = datePickerRoot.querySelector('input') || element;
            if (dateInput) {
                lastFocusedDateInput = dateInput;
                console.log('[F12 Paste] 📅 Date picker focused, tracking input');
            }
        }
        
        if (autocompleteRoot || datePickerRoot) {
            // Use requestAnimationFrame for faster execution (next frame instead of 200ms delay)
            requestAnimationFrame(() => {
                const input = (autocompleteRoot || datePickerRoot).querySelector('input') || element;
                if (input && input.value) {
                    let value = input.value.trim();
                    
                    // For Autocomplete, try to get actual value
                    if (autocompleteRoot && value) {
                        const selectedOption = autocompleteRoot.querySelector('[aria-selected="true"], .MuiAutocomplete-tag');
                        if (selectedOption) {
                            const optionValue = selectedOption.getAttribute('data-value') || 
                                              selectedOption.getAttribute('data-option-value');
                            if (optionValue) value = optionValue;
                        }
                    }
                    
                    if (value && value !== input.placeholder && value !== 'DD/MM/YYYY') {
                        storeValueForElement(input, value);
                    }
                }
            });
        }
    };
    
    window.addEventListener('focus', handleFocus, true);
    
    // Return uninstall function
    return function uninstall() {
        window.removeEventListener('keydown', handleF12KeyPress, true);
        window.removeEventListener('input', handleInputChange, true);
        window.removeEventListener('change', handleInputChange, true);
        document.removeEventListener('click', handleDatePickerDayClick, true);
        document.removeEventListener('click', handleDocumentClick, true);
        window.removeEventListener('focus', handleFocus, true);
        mutationObserver.disconnect();
        datePickerStateObserver.disconnect();
        // Clear storage on uninstall
        fieldValueStorage.clear();
    };
}

